import { useEffect, useState } from "react";
import Modal from "../../common/Modal";
import {
  getEligiblePayoutEvents,
  requestPayout,
} from "../../../services/walletService";
import { formatCurrency, formatDate } from "../../../utils/formatters";
import { useAuth } from "../../../context/AuthContext";
import { Calendar, TrendingUp } from "lucide-react";

const RequestPayoutModal = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setFetching(true);
      try {
        const res = await getEligiblePayoutEvents(user.id);
        setEvents(res);
        if (res.length > 0) setSelectedEventId(res[0].eventId);
      } finally {
        setFetching(false);
      }
    };
    fetch();
  }, [user.id]);

  const selectedEvent = events.find((e) => e.eventId === selectedEventId);

  const handleSubmit = async () => {
    if (!selectedEventId) return;
    setError("");
    setLoading(true);
    try {
      const res = await requestPayout(user.id, selectedEventId);
      if (res.isSuccess) {
        onSuccess();
        onClose();
      } else {
        setError(res.message ?? "Failed to request payout.");
      }
    } catch (err) {
      setError(err.response?.data?.message ?? "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Request Payout">
      <div className="space-y-5">
        {fetching ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-10 bg-bg-elevated rounded-xl" />
            <div className="h-24 bg-bg-elevated rounded-xl" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-text-muted">
              No eligible events for payout.
            </p>
            <p className="text-xs text-text-hint mt-1">
              Events must be completed with ticket sales to be eligible.
            </p>
          </div>
        ) : (
          <>
            {/* Event selector */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Select Event
              </label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full bg-bg-base border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary transition"
              >
                {events.map((e) => (
                  <option key={e.eventId} value={e.eventId}>
                    {e.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected event summary */}
            {selectedEvent && (
              <div className="bg-bg-base border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-1.5 text-xs text-text-muted">
                  <Calendar size={11} className="text-primary" />
                  {formatDate(selectedEvent.eventDate)}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-muted">
                    Total Earnings
                  </span>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp size={13} className="text-primary" />
                    <span className="text-sm font-bold text-primary">
                      {formatCurrency(selectedEvent.totalEarnings)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-text-hint">
                  Payouts are processed within 1–3 business days after approval.
                </p>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm border border-border text-text-muted hover:border-border-hover transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedEventId}
                className="flex-1 py-2.5 rounded-xl text-sm ingroove-gradient-bg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-glow"
              >
                {loading
                  ? "Requesting..."
                  : `Request ${formatCurrency(selectedEvent?.totalEarnings ?? 0)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RequestPayoutModal;
